import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { ProjectService } from '@/services/ProjectService';
import ProjectHeader from '@/components/header';
import ProjectRow from '@/components/summaryComponents/ProjectRow';
import CollectionDetailsSection from '@/components/summaryComponents/CollectionDetailsSection';

const CollectionSummary = () => {
    const navigate = useNavigate();
    const { collectionId } = useParams();
    const [projects, setProjects] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState(new Set());
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [collectionData, setCollectionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [collectionDetails, setCollectionDetails] = useState(null);
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const collectionProjects = await ProjectService.getCollectionData(collectionId);
                const fetchedProjects = collectionProjects.data.projects;
                const collectionDetails = await ProjectService.getCollectionDetails(collectionId);
                // console.log('Collection Details vs Projects:', collectionDetails, collectionProjects);
                const collectionDetailsData = collectionDetails.data;
                setProjects(fetchedProjects);
                setCollectionDetails(collectionDetailsData);
                setCollectionData(collectionProjects);

                // Preselect projects with >60% completion and not submitted
                const preselectedProjects = new Set(
                    fetchedProjects
                        .filter(project => {
                            const progress = (project.completedFlows?.length / 5) * 100;
                            return progress > 60 && project.status !== 'submitted';
                        })
                        .map(project => project.id)
                );
                setSelectedProjects(preselectedProjects);
            } catch (error) {
                console.error('Error fetching collection projects:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProjects();
    }, [collectionId]);

    const handleProjectSelect = (projectId) => {
        // Find the project to check its status
        const project = projects.find(p => p.id === projectId);
        if (project?.status === 'submitted') {
            return; // Early return if project is already submitted
        }

        const newSelected = new Set(selectedProjects);
        if (newSelected.has(projectId)) {
            newSelected.delete(projectId);
        } else {
            newSelected.add(projectId);
        }
        setSelectedProjects(newSelected);
    };

    const handleViewDetails = (projectId) => {
        navigate(`/project/${projectId}`);
    };

    const handleSubmitSelected = async () => {
        try {
            await ProjectService.submitProjects([...selectedProjects]);
            // Update local project status after successful submission
        setProjects(projects.map(project => {
                    if (selectedProjects.has(project.id)) {
                        return { ...project, status: 'submitted' };
                    }
                    return project;
        }));
            setShowConfirmation(true);
        } catch (error) {
            console.error('Error submitting projects:', error);
        }
    };

    const handleDone = () => {
        setShowConfirmation(false);
        navigate('/projects');
    };

    // Calculate submittable projects (excluding already submitted ones)
    const submittableProjects = projects.filter(project => project.status !== 'submitted');

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 pb-24">
        <ProjectHeader/>
            <main className="pt-20 px-4 max-w-4xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2 first-letter:uppercase">
                        {collectionData?.data.name} Summary
                    </h1>
                    <p className="text-gray-600">Select the room you want to submit for design and quote.</p>
                </div>
                
                <CollectionDetailsSection collectionDetails={collectionDetails} />
                
                <div className="space-y-4">
                    {projects.map(project => (
                        <ProjectRow
                            key={project.id}
                            project={project}
                            isSelected={selectedProjects.has(project.id)}
                            onSelect={handleProjectSelect}
                            onViewDetails={handleViewDetails}
                        />
                    ))}
                </div>

                <div className="mt-8 flex justify-between items-center">
                    <div className="text-gray-600">
                        {selectedProjects.size} of {submittableProjects.length} available projects selected
                    </div>
                    <Button
                        size="lg"
                        onClick={handleSubmitSelected}
                        disabled={selectedProjects.size === 0}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        Submit {selectedProjects.size} {selectedProjects.size === 1 ? 'project' : 'projects'}
                    </Button>
                </div>

                <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
                    <AlertDialogContent className="max-w-md">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="text-2xl font-bold text-center mb-2">
                                Projects Submitted Successfully! 🎉
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-center space-y-4">
                                <p>Thank you for submitting your projects!</p>
                  <p>Our team will review your requirements and provide you with custom designs and detailed quotes.</p>
                                <p className="font-medium">Please check your email for updates on your projects.</p>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex justify-center">
                            <AlertDialogAction
                                onClick={handleDone}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                Done
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </main>
        </div>
    );
};

export default CollectionSummary;